/** Makes one repository-backed portfolio snapshot available to every public route. */
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { fallbackPortfolio } from '../../data/sources/fallbackPortfolio';
import type { PortfolioSnapshot } from '../../domain/entities/portfolio';

interface PortfolioContextValue {
  readonly data: PortfolioSnapshot;
  readonly isRefreshing: boolean;
  readonly source: 'fallback' | 'firestore';
}

const PortfolioContext = createContext<PortfolioContextValue | null>(null);

export function PortfolioProvider({ children }: { readonly children: ReactNode }) {
  const [data, setData] = useState<PortfolioSnapshot>(fallbackPortfolio);
  const [isRefreshing, setIsRefreshing] = useState(true);
  const [source, setSource] = useState<'fallback' | 'firestore'>('fallback');

  useEffect(() => {
    let active = true;

    import('../../data/repositories/FirebasePortfolioRepository')
      .then(({ FirebasePortfolioRepository }) => new FirebasePortfolioRepository().getPortfolio())
      .then((remotePortfolio) => {
        if (!active) return;
        setData(remotePortfolio);
        setSource('firestore');
      })
      .catch((error: unknown) => {
        // The fallback keeps the public site usable; diagnostics remain available to developers.
        console.warn('Portfolio data could not be refreshed from Firestore.', error);
      })
      .finally(() => {
        if (active) setIsRefreshing(false);
      });

    return () => {
      active = false;
    };
  }, []);

  const value = useMemo(() => ({ data, isRefreshing, source }), [data, isRefreshing, source]);

  return <PortfolioContext.Provider value={value}>{children}</PortfolioContext.Provider>;
}

// Context and its access hook intentionally share one module so their private value type stays local.
// eslint-disable-next-line react-refresh/only-export-components
export function usePortfolio(): PortfolioContextValue {
  const context = useContext(PortfolioContext);
  if (!context) throw new Error('usePortfolio must be used inside PortfolioProvider.');
  return context;
}
